import { ASSISTANT_COLORS } from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import { Checkbox } from '@crowdex/ui/components/checkbox';
import { Input } from '@crowdex/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crowdex/ui/components/select';
import { Textarea } from '@crowdex/ui/components/textarea';
import { cn } from '@crowdex/ui/lib/utils';
import { Plus } from 'lucide-react';
import { useId, useState } from 'react';
import { startNewPersona } from '@/components/settings-page/personas-settings';
import { useShellNavigation } from '@/components/sidebar/shell-navigation';
import { assistantColorName } from '@/features/assistants/assistant-model';
import { openAssistant, useWorkspaceDirectory } from '@/lib/assistants/directory';
import { useRouter } from '@/lib/navigation';
import { settingsSectionPath } from '@/lib/settings-page/sections';
import {
  type PersonaOption,
  useAvatars,
  useKnownRepositories,
  usePersonas,
} from './workspace-resources';
import { type DialogFailure, dialogFailure, SheetField, WorkspaceSheet } from './workspace-sheet';

/** A row with a checkbox, as the boards list agents and repositories. */
function CheckRow({
  checked,
  onChange,
  label,
  detail,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  detail?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex min-h-9 items-center gap-3">
      <Checkbox
        checked={checked}
        className="size-[18px]"
        disabled={disabled}
        id={id}
        onCheckedChange={(next) => onChange(next === true)}
      />
      <label className="min-w-0 flex-1 cursor-pointer" htmlFor={id}>
        <span className="block truncate text-foreground">{label}</span>
        {detail !== undefined && (
          <span className="type-caption block text-muted-foreground">{detail}</span>
        )}
      </label>
    </div>
  );
}

function toggle(list: readonly string[], id: string, on: boolean): string[] {
  return on ? [...new Set([...list, id])] : list.filter((entry) => entry !== id);
}

/** The avatar choice that is none: the default voice. */
const NO_AVATAR = 'none';

/**
 * New assistant (D69): name, goal, colour, its avatar (a persona of the call plugin, taken from
 * the assistant that had it), its agents (the account's saved agents) and, optionally,
 * repositories among the user's. Created, its channel opens.
 */
export function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const navigation = useShellNavigation();
  const router = useRouter();
  const projects = useWorkspaceDirectory((state) => state.projects);
  const createAssistant = useWorkspaceDirectory((state) => state.createAssistant);
  const known = useKnownRepositories();
  const personas = usePersonas();
  const avatars = useAvatars();
  const nameId = useId();
  const goalId = useId();
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [color, setColor] = useState<string>(ASSISTANT_COLORS[0]);
  const [avatarId, setAvatarId] = useState<string | null>(null);
  const [members, setMembers] = useState<string[] | null>(null);
  const [repositoryIds, setRepositoryIds] = useState<string[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<DialogFailure | null>(null);
  // Until chosen: every agent, and no repository.
  const chosenMembers = members ?? personas.data.map((persona) => persona.id);
  const chosenRepositories = repositoryIds ?? [];

  const submit = async () => {
    setSubmitting(true);
    setFailure(null);
    try {
      const project = await createAssistant({
        name: name.trim(),
        goal: goal.trim(),
        color,
        repositoryIds: chosenRepositories,
        memberAgentIds: chosenMembers,
        avatarId,
      });
      onClose();
      await openAssistant(project, navigation.openConversation);
    } catch (reason) {
      setFailure(dialogFailure(reason));
      setSubmitting(false);
    }
  };

  return (
    <WorkspaceSheet
      canSubmit={name.trim() !== ''}
      failure={failure}
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Create assistant"
      submitting={submitting}
      title="New assistant"
      wide
    >
      <SheetField htmlFor={nameId} label="Name">
        <Input
          autoFocus
          className="h-10 rounded-xl bg-card"
          id={nameId}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
          placeholder="Onboarding v2"
          value={name}
        />
      </SheetField>

      <SheetField htmlFor={goalId} label="Goal">
        <Textarea
          className="min-h-14 rounded-xl bg-card px-3 py-2.5"
          id={goalId}
          maxLength={2000}
          onChange={(event) => setGoal(event.target.value)}
          placeholder="Rework the first launch: fewer steps, connect GitHub first, sample task."
          value={goal}
        />
      </SheetField>

      <div className="grid gap-5 sm:grid-cols-2">
        <SheetField label="Colour">
          <fieldset aria-label="Colour" className="flex flex-wrap gap-2">
            {ASSISTANT_COLORS.map((swatch) => (
              <button
                aria-pressed={color === swatch}
                aria-label={assistantColorName(swatch)}
                className={cn(
                  'size-6 rounded-md ring-offset-2 ring-offset-background',
                  color === swatch && 'ring-2 ring-foreground',
                )}
                key={swatch}
                onClick={() => setColor(swatch)}
                // The project's colour is its data, not a theme colour.
                style={{ backgroundColor: swatch }}
                type="button"
              />
            ))}
          </fieldset>
        </SheetField>
        <SheetField label="Avatar">
          <Select
            onValueChange={(value) => setAvatarId(value === NO_AVATAR ? null : value)}
            value={avatarId ?? NO_AVATAR}
          >
            <SelectTrigger aria-label="Avatar" className="h-10 w-full rounded-xl bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_AVATAR}>None</SelectItem>
              {avatars.data.map((avatar) => {
                // Another assistant's moves to this one.
                const usedBy = projects.find(
                  (project) => project.avatarId === avatar.id && project.archivedAt === null,
                )?.name;
                return (
                  <SelectItem key={avatar.id} value={avatar.id}>
                    {usedBy === undefined ? avatar.name : `${avatar.name} · ${usedBy}`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </SheetField>
      </div>

      <SheetField label="Agents">
        <PersonaChoices
          chosen={chosenMembers}
          error={personas.error}
          loading={personas.status === 'loading'}
          onChange={setMembers}
          onNewPersona={() => {
            onClose();
            startNewPersona();
          }}
          onOpenPersonas={() => {
            onClose();
            router.push(settingsSectionPath('personas'));
          }}
          personas={personas.data}
        />
      </SheetField>

      <SheetField label="Repositories">
        {known.length === 0 ? (
          <p className="type-meta text-muted-foreground">No repository yet.</p>
        ) : (
          <div>
            {known.map((repository) => (
              <CheckRow
                checked={chosenRepositories.includes(repository.id)}
                key={repository.id}
                label={repository.name}
                onChange={(on) => setRepositoryIds(toggle(chosenRepositories, repository.id, on))}
              />
            ))}
          </div>
        )}
      </SheetField>
    </WorkspaceSheet>
  );
}

function PersonaChoices({
  personas,
  chosen,
  onChange,
  onOpenPersonas,
  onNewPersona,
  loading,
  error,
}: {
  personas: readonly PersonaOption[];
  chosen: readonly string[];
  onChange: (ids: string[]) => void;
  onOpenPersonas: () => void;
  onNewPersona: () => void;
  loading: boolean;
  error: string | null;
}) {
  if (error !== null)
    return <p className="type-meta text-destructive">The agents could not be read: {error}</p>;
  if (loading) return <p className="type-meta text-muted-foreground">Reading your agents…</p>;
  if (personas.length === 0)
    return (
      <div className="type-meta space-y-2 text-muted-foreground">
        <p>
          No agent yet.{' '}
          <button
            className="underline underline-offset-2 hover:text-foreground"
            onClick={onOpenPersonas}
            type="button"
          >
            Settings › Personas
          </button>
        </p>
        <Button onClick={onNewPersona} size="sm" type="button" variant="outline">
          <Plus />
          New agent
        </Button>
      </div>
    );
  return (
    <div>
      {personas.map((persona) => (
        <CheckRow
          checked={chosen.includes(persona.id)}
          key={persona.id}
          label={`${persona.name} · ${persona.agentName}`}
          onChange={(on) => onChange(toggle(chosen, persona.id, on))}
        />
      ))}
    </div>
  );
}
